import React, { useState, useEffect } from "react";
import { Activity, Users, TrendingUp, Eye, Calendar } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const OrgAnalyticsCharts = ({ orgId, getAccessToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [programData, setProgramData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!orgId) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = getAccessToken();
        
        // Fetch summary analytics
        const summaryResponse = await fetch(
          `${API_URL}/v2/analytics/organizations/${orgId}/views?days=${days}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!summaryResponse.ok) {
          throw new Error(`Failed to fetch summary: ${summaryResponse.status}`);
        }

        const summaryData = await summaryResponse.json();
        setAnalytics(summaryData);

        // Fetch time series data
        const timeSeriesResponse = await fetch(
          `${API_URL}/v2/analytics/organizations/${orgId}/views/timeseries?days=${days}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!timeSeriesResponse.ok) {
          throw new Error(`Failed to fetch timeseries: ${timeSeriesResponse.status}`);
        }

        const timeSeriesResult = await timeSeriesResponse.json();
        
        // Format time series data for chart
        const formattedTimeSeries = (timeSeriesResult.data || []).map(item => ({
          date: new Date(item.date).toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric" 
          }),
          fullDate: item.date,
          views: item.total_views || 0,
          uniqueViewers: item.unique_viewers || 0,
        }));
        setTimeSeriesData(formattedTimeSeries);

        // Prepare program data for bar chart
        if (summaryData.summary?.topPrograms) {
          const formattedProgramData = summaryData.summary.topPrograms
            .slice(0, 5)
            .map(program => ({
              name: program.name.length > 20
                ? `${program.name.substring(0, 20)}...`
                : program.name,
              fullName: program.name,
              views: program.views || 0,
              conversionRate: program.conversionRate || 0,
            }));
          setProgramData(formattedProgramData);
        }
      } catch (err) {
        console.error("Error fetching view analytics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [orgId, days, getAccessToken]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-300 text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={`${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ProgramTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = programData.find(p => p.name === label);
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl max-w-xs">
          <p className="text-white font-medium mb-2">{data?.fullName || label}</p>
          {payload.map((entry) => (
            <p key={`${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
              {entry.name === "Conversion Rate" ? "%" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 bg-gray-800 rounded-lg flex items-center gap-2 text-gray-400">
        <Activity className="w-4 h-4 animate-spin" />
        Loading view analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        Error loading analytics: {error}
      </div>
    );
  }

  if (!analytics || !analytics.summary) {
    return (
      <div className="p-4 bg-gray-800/50 rounded-lg text-gray-400">
        No view data available for this organization.
      </div>
    );
  }

  const { summary, activeViewers } = analytics;
  const { overview } = summary;

  // Calculate average
  const avgViewsPerDay = timeSeriesData.length > 0 
    ? (timeSeriesData.reduce((sum, day) => sum + day.views, 0) / timeSeriesData.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium flex items-center gap-2">
          <Eye className="w-5 h-5 text-indigo-400" />
          View Analytics Dashboard
        </h3>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-gray-700 border border-gray-600 text-white text-sm rounded px-3 py-1"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Eye className="w-4 h-4" />
            Total Views
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.totalViews?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Unique Viewers
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.uniqueViewers?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Conversion Rate
          </div>
          <div className="text-2xl font-bold text-white">
            {overview.conversionRate?.toFixed(1) || 0}%
          </div>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Activity className="w-4 h-4" />
            Currently Viewing
          </div>
          <div className="text-2xl font-bold text-green-400">
            {activeViewers?.orgTotal || 0}
          </div>
        </div>
      </div>

      {/* Views Over Time Chart */}
      {timeSeriesData.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-400" />
              Views Over Time
            </h4>
            <div className="text-sm text-gray-400">
              Avg: {avgViewsPerDay} views/day
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorUnique" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="views"
                  name="Total Views"
                  stroke="#818cf8"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorViews)"
                />
                <Area
                  type="monotone"
                  dataKey="uniqueViewers"
                  name="Unique Viewers"
                  stroke="#34d399"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorUnique)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Programs Chart */}
      {programData.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <h4 className="text-white font-medium mb-4">Top Programs by Views</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={programData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                <XAxis 
                  type="number" 
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#9ca3af"
                  fontSize={11}
                  width={120}
                  tickLine={false}
                />
                <Tooltip content={<ProgramTooltip />} />
                <Bar 
                  dataKey="views" 
                  name="Views" 
                  fill="#818cf8"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Trend Indicator */}
      {overview.trend && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Trend:</span>
          <span
            className={`font-medium ${
              overview.trend.direction === "up"
                ? "text-green-400"
                : overview.trend.direction === "down"
                ? "text-red-400"
                : "text-gray-400"
            }`}
          >
            {overview.trend.direction === "up" ? "↑" : overview.trend.direction === "down" ? "↓" : "→"}
            {" "}
            {overview.trend.percentChange?.toFixed(1)}%
          </span>
          <span className="text-gray-500">vs previous period</span>
        </div>
      )}

      {/* Currently Viewing Program */}
      {activeViewers?.topProgram && (
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
          <h4 className="text-indigo-300 font-medium mb-2">Most Viewed Right Now</h4>
          <p className="text-white">{activeViewers.topProgram.name}</p>
          <p className="text-indigo-400 text-sm">
            {activeViewers.topProgram.viewers} people viewing
          </p>
        </div>
      )}
    </div>
  );
};

export default OrgAnalyticsCharts;
